import { Button } from "@timeghost/ui/shadcn/button";
import { RotateCcw, Save } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

const DISCARD_ICON = <RotateCcw className="size-4" />;
const SAVE_ICON = <Save className="size-4" />;
const EMPTY_ERRORS: readonly string[] = [];

interface FormSubmitToolbarProps {
	readonly canSave?: boolean;
	readonly className?: string;
	readonly discardLabel?: string;
	readonly errors?: readonly string[];
	readonly onDiscard: () => void;
	readonly onSave: () => void | Promise<void>;
	readonly saveLabel?: string;
	readonly saving?: boolean;
	readonly unsavedLabel?: string;
	readonly visible: boolean;
}

/** Floating discard/save pill for dirty forms. Hidden when `visible` is false. */
export default function FormSubmitToolbar({
	canSave = true,
	className,
	discardLabel,
	errors = EMPTY_ERRORS,
	onDiscard,
	onSave,
	saveLabel,
	saving = false,
	unsavedLabel,
	visible,
}: FormSubmitToolbarProps): JSX.Element | null {
	const { t } = useTranslation();

	if (!visible) {
		return null;
	}

	const hasErrors = errors.length > 0;

	return (
		<div
			aria-live="polite"
			className={cn(
				"pointer-events-none fixed inset-x-4 bottom-5 z-50 flex justify-center sm:bottom-7",
				"slide-in-from-bottom-5 scale-100 animate-in duration-300 ease-out",
				className
			)}
			data-slot="form-submit-toolbar"
			role="status">
			<div className="pointer-events-auto flex w-full max-w-md flex-col items-stretch gap-2 sm:w-auto sm:max-w-lg">
				{hasErrors ? (
					<ul
						className="list-disc space-y-1 rounded-xl border border-destructive/30 bg-background/95 px-4 py-2 pl-6 text-destructive text-xs shadow-lg backdrop-blur supports-backdrop-filter:bg-background/80"
						data-slot="form-submit-toolbar-errors">
						{errors.map((message) => (
							<li key={message}>{message}</li>
						))}
					</ul>
				) : null}
				<div className="flex items-center gap-1.5 rounded-full border border-border bg-background/95 p-1.5 pl-4 shadow-lg backdrop-blur supports-backdrop-filter:bg-background/80">
					<span className="mr-auto min-w-0 truncate pr-2 text-muted-foreground text-sm">
						{unsavedLabel ?? t("settings.editSession.unsaved", { count: "" })}
					</span>
					<Button
						className="rounded-full"
						disabled={saving}
						leadingIcon={DISCARD_ICON}
						onClick={onDiscard}
						size="sm"
						type="button"
						variant="ghost">
						{discardLabel ?? t("settings.editSession.discard")}
					</Button>
					<Button
						className="rounded-full"
						disabled={!canSave || saving}
						leadingIcon={SAVE_ICON}
						loading={saving}
						onClick={() => {
							void onSave();
						}}
						size="sm"
						type="button">
						{saveLabel ?? t("settings.editSession.save")}
					</Button>
				</div>
			</div>
		</div>
	);
}
